<template>
  <div class="q-pa-md">
    <q-card>
      <q-card-section class="row border-bottom items-center full-width q-py-xs q-pl-none">
        <q-legend
          :label="$t('Supplies')"
          text-class="text-h6"
        />

        <search
          dense
          autoset
          is-expandable
          @submit="handleSearch"
        />

        <q-space />

        <q-btn
          color="dark"
          text-color="white"
          :label="$t('Print barcodes')"
          size="sm"
          class="q-mr-sm"
          no-caps
          unelevated
          @click="printBarcodes(supplies.map(x => x.barcode))"
        />

        <q-btn
          :color="serverParams.filter && serverParams.filter.length > 0 ? 'light-blue-9' : 'dark'"
          text-color="white"
          size="sm"
          class="q-mr-sm"
          :label="filterBtnText"
          no-caps
          unelevated
          @click="toggleFilters"
        />

        <q-btn
          color="dark"
          text-color="white"
          :label="$t('Refresh')"
          class="q-mr-sm"
          size="sm"
          no-caps
          unelevated
          @click="refreshItems"
        />

        <q-btn-dropdown
          color="light-blue-9"
          size="sm"
          icon="add"
        >
          <q-list>
            <q-item clickable v-close-popup @click="handleCreate('prep')">
              <q-item-section>
                <q-item-label>
                  {{ $t('Prep&Pack') }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup @click="handleCreate('labelling')">
              <q-item-section>
                <q-item-label>
                  {{ $t('Labelling') }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup @click="handleCreate('package')">
              <q-item-section>
                <q-item-label>
                  {{ $t('Package') }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup @click="handleCreate('attachment')">
              <q-item-section>
                <q-item-label>
                  {{ $t('Attachment') }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup @click="handleCreate('shipping')">
              <q-item-section>
                <q-item-label>
                  {{ $t('Shipping') }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </q-card-section>

      <filter-collapse
        :is-open="isOpen"
        :options="{
          defaultFilter: serverParams.filter,
          fields: activatedFields,
          values: {
            states: statuses
          },
          style: {
            noGroups: true
          }
        }"
        @submit="handleFiltersSubmit"
        @close="toggleFilters"
      />

      <q-card-section class="q-ma-none q-pa-none">
        <q-table
          style="height: calc(100vh - 130px);"
          class="sticky-header-table"
          row-key="id"
          :rows-per-page-label="$t('Rows per page')"
          :rows="supplies"
          :columns="columns"
          v-model:pagination="pagination"
          :loading="supplyLoading"
          :filter="filter"
          virtual-scroll
          binary-state-sort
          flat
          @request="onRequest"
        >
          <template v-slot:loading>
            <q-inner-loading
              showing
              color="primary"
            />
          </template>

          <template v-slot:body="props">
            <q-tr
              :props="props"
              class="clickable"
              @dblclick="onRowDblClick(props.row)"
            >
              <q-td
                key="id"
                :props="props"
                class="text-subtitle1"
              >
                <strong>{{ props.row.id }}</strong>
              </q-td>

              <q-td
                key="name"
                :props="props"
                class="text-subtitle1"
              >
                {{ props.row.name}}
              </q-td>

              <q-td
                key="type"
                :props="props"
                >
                {{props.row.type}}
              </q-td>

              <q-td
                key="access"
                :props="props"
              >
                  <super-dropdown :model="schema(props.row)"  />
              </q-td>

              <q-td
                key="warehouse"
                :props="props"
              >
                <div v-if="props.row._embedded.warehouse">
                  {{ `${props.row._embedded.warehouse.name} (${props.row._embedded.warehouse.id})` }}
                </div>
              </q-td>

              <q-td
                key="barcode"
                :props="props"
              >
                {{ props.row.barcode }}
              </q-td>

              <q-td
                key="dimensions"
                :props="props"
              >
                <span v-if="props.row.dimensions && typeof props.row.dimensions === 'object'">
                  {{ `${props.row.dimensions.x || 0}/${props.row.dimensions.y || 0}/${props.row.dimensions.z || 0}` }}
                </span>
              </q-td>

              <q-td
                key="price"
                :props="props"
              >
                {{ props.row.price }}
              </q-td>

              <q-td
                key="created"
                :props="props"
              >
                {{ $moment(props.row.created.date).format(appOptions.formats.date) }}
              </q-td>

              <q-td
                key="state"
                :props="props"
              >
                <q-toggle
                  color="light-blue-9"
                  :model-value="props.row.state === 'active'"
                  @update:model-value="handleStateChange(props.row)"
                />
              </q-td>

              <q-td
                key="label"
                :props="props"
              >
                <q-btn
                  color="dark"
                  text-color="white"
                  :label="$t('Print')"
                  size="sm"
                  no-caps
                  unelevated
                  @click="printBarcodes([props.row.barcode])"
                />
              </q-td>
            </q-tr>
          </template>
        </q-table>
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
// Vuex
import { mapActions, mapGetters, mapMutations } from 'vuex'

// Components
import Search from '../../components/search/Search.vue'
import FilterCollapse from '../../components/filters/FilterCollapse.vue'

// Utils
import { buildQuery } from '../../utils/query-utils'

// Mixins
import TableMixin from './../../components/global/TableMixin'
import SuperDropdown from "@/apps/app/components/super-dropdown/SuperDropdown.vue";

export default {
  name: 'Supplies',
  components: {
      SuperDropdown,
    Search,
    FilterCollapse
  },
  mixins: [
    TableMixin
  ],
  data () {
    return {
      isOpen: false,
      filter: '',
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 25,
        rowsNumber: 25
      },
      columns: [
        {
          label: this.$t('Id'),
          name: 'id',
          align: 'left'
        },
        {
          label: this.$t('Name'),
          name: 'name',
          align: 'left'
        },
        {
          label: this.$t('Type'),
          name: 'type',
          align: 'left'
        },
        {
          label: this.$t('Access'),
          name: 'access',
          align: 'left'
        },
        {
          label: this.$t('Warehouse'),
          name: 'warehouse',
          align: 'left'
        },
        {
          label: this.$t('Barcode'),
          name: 'barcode',
          align: 'left'
        },
        {
          label: this.$t('Dimensions'),
          name: 'dimensions',
          align: 'left'
        },
        {
          label: this.$t('Price'),
          name: 'price',
          align: 'left'
        },
        {
          label: this.$t('Created'),
          name: 'created',
          align: 'left'
        },
        {
          label: this.$t('Status'),
          name: 'state',
          align: 'left'
        },
        {
          label: this.$t('Label'),
          name: 'label',
          align: 'center'
        }
      ],
      stateColors: {
        active: 'success',
        inactive: 'dark'
      },
      statuses: [
        { id: 'active', title: this.$t('Active') },
        { id: 'inactive', title: this.$t('Inactive') }
      ],
      accessStates: [
        {
          buttons: [
            { id: false, title: 'Private', transitions: [true], color: 'lime-7' },
            { id: true, title: 'Public', transitions: [false], color: 'light-blue-7' }
          ]
        }
      ],
      activatedFields: [
        'id',
        'state',
        'sender',
        'warehouse',
        'barcode',
        'price',
        'order',
        'created.from',
        'created.to'
      ]
    }
  },
  computed: {
    ...mapGetters([
      'supplies',
      'totalSuppliesNumber',
      'supplyLoading',
      'appOptions'
    ]),
    schema () {
        return (row) => {
            const value = this.accessStates[0].buttons.find(({ id }) => id == row.isPublic) || { id: 'false', title: 'Private' }
            const option = {
                id: 'statuses',
                type: 'super-dropdown',
                variant: 'link',
                color: value.color || 'default',
                onClick: (state) => {
                    this.handleAccessChange(row, state)
                },
                value,
                options: this.accessStates
            }

            return option

        }

    },
    filterBtnText () {
      return this.serverParams.filter && this.serverParams.filter.length > 0
        ? this.$t('Filtered: ' + this.totalSuppliesNumber)
        : this.$t('Filter')
    }
  },
  mounted () {
    this.loadDefaultItems()
  },
  methods: {
    ...mapActions([
      'loadSupplies'
    ]),
    ...mapMutations([
      'setSupply',
      'upsertSupply'
    ]),
    refreshItems () {
      return this.onRequest({
        pagination: {
          forceReload: true
        }
      })
    },
    printBarcodes (barcodes) {
      this.$service.printer.generatePDFBarcodes(barcodes.map(barcode => {
        return {
          size: '30x30',
          barcode,
          forceNoAutoClose: true
        }
      }))
    },
    handleFiltersSubmit (filter) {
      this.isOpen = false
      return this.onRequest({ pagination: { filter, page: 1 } })
    },
    toggleFilters () {
      this.isOpen = !this.isOpen
    },
    handleAccessChange (item, value) {
        if (value === false) {
            return this.$service.supply.save({ isPublic: 0 }, item.id)
                .then(item => {
                    this.upsertSupply(item)
                })
        } else if (value === true) {
            return this.$service.supply.save({ isPublic: 1 }, item.id)
                .then(item => {
                    this.upsertSupply(item)
                })
        }
    },
    handleStateChange (item) {
      const invertedState = {
        active: 'inactive',
        inactive: 'active'
      }

      const state = invertedState[item.state]

      return this.$service.supply.save({ state }, item.id)
        .then(item => {
          this.upsertSupply(item)
        })
    },
    loadDefaultItems () {
      return this.onRequest({})
    },
    onRequest (data = {}) {
      this.pagination = data.pagination || {}
      const query = buildQuery(this.pagination)
      query['order-by'] = [
        { type: 'field', field: 'id', direction: 'desc' }
      ]
      query.filter=[{ type: 'eq', field: 'state', value: 'active' }]
      this.updateParams(query)

      return this.loadSupplies(query)
        .then(({ items, totalPages, page, totalItems }) => {
          this.pagination = {
            ...this.pagination,
            page,
            rowsNumber: totalItems
          }

          return { items, totalPages, page, totalItems }
        })
    },
    handleSearch (search) {
      return this.onRequest({ pagination: { page: 1, search } })
    },
    handleCreate (type) {
      this.$router.push(`/storage/supplies/entity/${type}`)
    },
    onRowDblClick (row) {
      this.setSupply(row)
      this.$router.push(`/storage/supplies/entity/${row.type}/${row.id}`)
    }
  }
}
</script>
